<template>
  <div class="ProjectImage" :class="[{zoom:canZoom}, {shadow:hasShadow}, {customSize:hasSize}]">
    <div class="image" @click="toggleImage" :style="maxSize">
      <img v-lazy="srcImage" :data-srcset="srcSet" :sizes="srcSizes" class="main">
      <transition name="vFade">
        <div class="loading" v-if="!mainLoaded"><svg><use xlink:href="#loading"></use></svg></div>
      </transition>
  
      <div class="caption" v-if="caption != null">
        <div class="text" v-html="caption"></div>
        <div class="zoom" role="button" tabindex="0" v-if="canZoom"><svg><use xlink:href="#searchPlus"></use></svg></div>
      </div>
    </div>

    <transition name="vFadeMobile">
      <div class="imageOverlay" v-if="overlayOpen" @click="toggleImage">
        <div class="close" role="button" tabindex="0"><span></span><span></span></div>
        <div class="image">
          <img v-lazy="largeImage" class="over">
          <transition name="vFade">
            <div class="loading" v-if="!overlayLoaded"><svg><use xlink:href="#loading"></use></svg></div>
          </transition>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ProjectImage',
  props: {
    srcImage: {
      type: String
    },
    srcSize: {
      type: Number,
      default: null
    },
    largeImage: {
      type: String
    },
    caption: {
      type: String,
      default: null
    },
    canZoom: {
      type: Boolean,
      default: false
    },
    hasShadow: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      overlayOpen: false,
      mainLoaded: false,
      overlayLoaded: false
    }
  },
  mounted() {
    // globally register lazyload listner to fire
    // when images are loaded
    this.$Lazyload.$on('loaded', (el) => {
      this.handleLoading(el)
    })
  },
  destroyed () {
    this.$Lazyload.$off('loaded')
  },
  methods: {
    toggleImage() {
      if (this.canZoom) {
        let windowWidth = window.innerWidth
        if (windowWidth > 900) {
          if (this.overlayOpen == true) {
            this.overlayOpen = false
            this.overlayLoaded = false
          } else {
            this.overlayOpen = true
          }
        }
      }
    },
    // turn loading animaitons off/on depending on whether
    // the image has loaded
    handleLoading(el) {
      let currentSrc = el.src
      let currentClass = el.el.className
      // console.log(el.el.className)
      // console.log('el.src = ' + currentSrc)
      // console.log('this.srcImage1 = ' + this.srcImage)
      if (currentClass == 'main') {
        if (currentSrc == this.srcImage || currentSrc == this.largeImage) {
          this.mainLoaded = true
        }
      }
      if (currentClass == 'over') {
        if (currentSrc == this.largeImage) {
          this.overlayLoaded = true
        }
      }
    }
  },
  computed: {
    srcSet() {
      if (this.srcSize != null) {
        return this.srcImage + ' ' + this.srcSize + 'w, ' + this.largeImage + ' 1800w'
      } else {
        return this.srcImage + ' 900w, ' + this.largeImage + ' 1800w'
      }
    },
    srcSizes() {
      if (this.srcSize != null) {
        return '(max-width: 900px) ' + this.srcSize + 'px, 900px'
      } else {
        return '(max-width: 900px) 100vw, 900px'
      }
    },
    maxSize() {
      if (this.srcSize != null) {
        return 'max-width:' + this.srcSize + 'px'
      } else {
        return ''
      }
    },
    hasSize() {
      if (this.srcSize != null) {
        return true
      } else {
        return false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ProjectImage {
  width: 100%;
  margin: 70px auto;
  color: #000;
  text-align: center;

  & .loading {
    position: absolute;
    width: 100px;
    height: 100px;
    left: 50%;
    top: 45%;
    transform: translate(-50%,-50%);

    & svg {
      width: 50px;
      height: 50px;
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
    }

    &::after {
      content: 'Loading';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 0;
      color: $greyDark;
      text-transform: uppercase;
      font-size: 12px;
    }
  }

  & > .image {
    width: 100%;
    position: relative;

    & > img {
      width: 100%;
      height: auto;
      position: relative;
      z-index: 5;
      transition: opacity 2000ms;

      &[lazy=loading] {
        opacity: 0;
      }
      &[lazy=loaded] {
        opacity: 1;
      }
    }

    & .caption {
      display: flex;
      justify-content: space-between;
      border-top: 1px solid $greyMed;
      margin: 25px 0 0 0;
      padding: 10px 0 0 0;

      & .text {
        font-size: 13px;
        @include fontItalic;
        line-height: 18px;
        color: $greyDark;
      }
      & .zoom {
        width: 18px;
        height: 18px;
        position: relative;
        cursor: pointer;

        & svg {
          width: 16px;
          height: 16px;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%,-50%);
          fill: $greyDark;
          transition: all 200ms;
        }
      }
    }
  }


  // has zoom
  &.zoom {
    cursor: zoom-in;

    &:hover {
      & .caption {
        & .zoom {
          & svg {
            fill: #000;
          }
        }
      }
    }
    @media screen and (max-width: $breakLarge) {
      cursor: default;
      & .caption {
        & .zoom {
          display: none;
        }
      }
    }
  }

  // has shadow
  &.shadow {
    & > .image {

      & > img {
        box-shadow: 0 0 25px rgba(0,0,0,0.2);
      }
    }
    & .imageOverlay {
      & > .image {
        & > img {
          box-shadow: 0 0 20px rgba(0,0,0,0.2);
        }
      } 
    }
  }

  // custom (smaller) sized image
  &.customSize {
    & > .image {
      width: auto;
      margin: 0 auto;

      & > img {
        width: auto;
        max-width: 100%;
        height: auto;
      }
    }
  }

  & .imageOverlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 100;
    background: rgba(255,255,255,0.95);
    text-align: center;
    cursor: zoom-out;

    & .close {
      height: 50px;
      width: 50px;
      border-radius: 25px;
      border: 1px solid #000;
      background: #fff;
      position: relative;
      z-index: 5;
      margin: 30px auto 0 auto;
      transform-origin: center;
      cursor: pointer;
      transition: all 300ms;

      &:focus {
        outline: none;
      }

      & span {
        display: block;
        width: 31px;
        height: 2px;
        background: #000;
        position: absolute;
        left: 9px;
        top: 24px;
        transform-origin: center;

        &:first-child {
          transform: rotate(45deg);
          animation: closeOne 500ms 1;
        }
        &:last-child {
          transform: rotate(135deg);
          animation: closeTwo 500ms 1;
        }
      }
      @keyframes closeOne {
        from {transform: rotate(45deg) scaleX(0);}
        to {transform: rotate(45deg) scaleX(1);}
      }
      @keyframes closeTwo {
        from {transform: rotate(135deg) scaleX(0);}
        to {transform: rotate(135deg) scaleX(1);}
      }
      &:hover {
        transform: scale(1.2);
      }
    }
    & > .image {
      position: absolute;
      top: 80px;
      left: 20px;
      right: 20px;
      bottom: 20px;
      transform-origin: center;
      animation: loadImage 1000ms forwards;

      & > img {
        max-width: 100%;
        max-height: 100%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%,-50%);
        z-index: 5;
        transition: opacity 1000ms;

        &[lazy=loading] {
          opacity: 0;
        }
        &[lazy=loaded] {
          opacity: 1;
        }
      }
      @keyframes loadImage {
        from {transform: scale(.5);}
        to {transform: scale(1);}
      }
    } 
  }
}

</style>
